import axios from 'axios'
import { canRetry } from '@/utils/requestRetry'
import { ElMessage, ElMessageBox } from 'element-plus'
import messages from '@/plugins/i18n/messages'
import { useAuthStore } from "@/stores/auth"
import { useNetworkStore } from "@/stores/network"
import router from '@/router'
import { getToken, removeToken, removeUserInfo } from '@/utils/auth'
import tokenManager from '@/utils/tokenManager'
import authInterceptor from '@/utils/authInterceptor'
import { getCurrentTime } from '@/utils/format'
import { getApiBaseUrl } from '@/utils/apiBaseUrl'

// API请求超时设置（毫秒）
const DEFAULT_TIMEOUT = 30000;      // 默认请求超时时间为30秒
const CHAT_TIMEOUT = 260000;        // 通用聊天请求超时时间为260秒(4分20秒),比后端240秒略长
const QUICK_MODE_CHAT_TIMEOUT = 600000; // 快速模式 start_brief（/chats/chat）10分钟，复杂靶点简报实测可达8分钟+
const TARGET_CHAT_TIMEOUT = 600000; // 🔧 修复：靶点聊天超时时间增加到600秒(10分钟)，支持临床试验搜索等复杂查询
const FILE_TIMEOUT = 45000;         // 文件操作超时时间为45秒
const DOWNLOAD_LOCAL_TIMEOUT = 360000; // 本地下载超时时间为6分钟（从OSS下载大文件需要更长时间）
const REGISTER_TIMEOUT = 60000;     // 注册请求超时时间为60秒（包含后台示例数据插入）
const ACTIVATION_TIMEOUT = 60000;   // ✅ 激活请求超时时间为60秒（避免数据库慢查询导致超时）
const TARGETS_LIST_TIMEOUT = 60000; // ✅ 靶点列表超时60秒（管理员首次查询/大数据量可能较慢）
const PREFERENCE_EXTRACT_TIMEOUT = 120000; // ✅ 偏好抽取超时时间为120秒（LLM分析需要较长时间）
const AGENT_TEST_TIMEOUT = 120000;  // ✅ 智能体测试超时时间为120秒（LLM推理需要较长时间，确保复杂查询也能完成）
const HALLUCINATION_DETECTION_TIMEOUT = 600000; // ✅ 幻觉检测超时10分钟（多智能体流水线耗时较长，与靶点聊天一致）
const HALLUCINATION_APPLY_FIXES_TIMEOUT = 600000; // 幻觉批量修复应用10分钟（LLM改写+保存+重排引用，实测可达4分钟+）
const HALLUCINATION_STATUS_TIMEOUT = 120000;    // 幻觉任务状态轮询请求超时2分钟（避免30秒误超时）
const HALLUCINATION_HISTORY_TIMEOUT = 120000;   // 幻觉历史查询超时2分钟（高并发/慢查询场景）

const INVALID_TOKEN_LITERALS = new Set(['undefined', 'null', 'none', ''])

function isValidJwtToken(token) {
  if (!token || typeof token !== 'string') return false
  const trimmed = token.trim()
  if (INVALID_TOKEN_LITERALS.has(trimmed.toLowerCase())) return false
  const parts = trimmed.split('.')
  return parts.length === 3 && parts.every((part) => part.length > 0)
}

/** 相同错误文案在此窗口内只弹一次（拦截器 + 业务 catch 共用） */
export const ERROR_TOAST_DEDUPE_MS = 3000
const recentErrorToasts = new Map()

const extractErrorMessage = (options) => {
  if (typeof options === 'string') return options.trim()
  if (options && typeof options.message === 'string') return options.message.trim()
  return ''
}

export const wasMessageRecentlyToasted = (message) => {
  const key = String(message || '').trim()
  if (!key) return false
  const last = recentErrorToasts.get(key)
  return last != null && Date.now() - last < ERROR_TOAST_DEDUPE_MS
}

/**
 * 按文案去重后展示 ElMessage.error；重复则返回 false。
 * 全局 patch 后业务层直接调 ElMessage.error 也会走同一去重逻辑。
 */
export const notifyApiErrorOnce = (options) => {
  const message = extractErrorMessage(options)
  if (!message) {
    _nativeElMessageError?.(options)
    return true
  }
  if (wasMessageRecentlyToasted(message)) return false

  const now = Date.now()
  recentErrorToasts.set(message, now)
  if (recentErrorToasts.size > 64) {
    for (const [key, ts] of recentErrorToasts) {
      if (now - ts > ERROR_TOAST_DEDUPE_MS) recentErrorToasts.delete(key)
    }
  }
  _nativeElMessageError?.(options)
  return true
}

export const isApiErrorToastShown = (error) => Boolean(
  error?.__toastShown || wasMessageRecentlyToasted(error?.message)
)

let _nativeElMessageError = null

const installElMessageErrorDedupe = () => {
  if (ElMessage.error.__apiDedupeInstalled) return
  _nativeElMessageError = ElMessage.error.bind(ElMessage)
  ElMessage.error = (options) => {
    notifyApiErrorOnce(options)
  }
  ElMessage.error.__apiDedupeInstalled = true
}

installElMessageErrorDedupe()

// i18n 文案获取（不依赖组件上下文）
const tOr = (key, fallback) => {
  try {
    const locale = (localStorage.getItem('locale') || 'zh-CN')
    const parts = key.split('.')
    let cur = messages?.[locale]
    for (const p of parts) {
      cur = cur?.[p]
      if (cur === undefined) break
    }
    if (typeof cur === 'string') return cur
  } catch {}
  return fallback
}

const getApiMessageFromDetail = (detail) => {
  if (!detail) return ''
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const first = detail[0]
    if (!first) return ''
    return first.message || first.msg || first.detail || ''
  }
  if (typeof detail === 'object') {
    return detail.message || detail.detail || detail.msg || detail.error || ''
  }
  return ''
}

const isKnowledgeBaseQuotaError = (data) => {
  const code = String(data?.code || '')
  return code.startsWith('KNOWLEDGE_BASE_') && code.endsWith('_LIMIT_EXCEEDED')
}

const formatKnowledgeBaseQuotaMessage = (data) => {
  if (!isKnowledgeBaseQuotaError(data)) return ''
  const detail = typeof data.detail === 'string' ? data.detail : '知识库文件数量将超过上限'
  const limit = data.limit ?? '未知'
  const used = data.used ?? '未知'
  const requested = data.requested ?? '未知'
  const available = data.available ?? 0
  return `${detail}（已用 ${used}/${limit}，本次 ${requested}，剩余 ${available}）`
}

const normalizeApiError = (error, fallback = '') => {
  const response = error?.response
  const data = response?.data
  const detail = data?.detail
  const message = (
    formatKnowledgeBaseQuotaMessage(data) ||
    getApiMessageFromDetail(detail) ||
    data?.error?.message ||
    data?.message ||
    data?.msg ||
    fallback ||
    error?.message ||
    tOr('errors.unknown', '未知错误')
  )

  return {
    status: response?.status,
    message,
    detail,
    data,
    quota: isKnowledgeBaseQuotaError(data) ? data : null,
    isNetworkError: !response
  }
}

const rejectApiError = (error, fallback = '') => {
  const normalized = normalizeApiError(error, fallback)
  error.message = normalized.message
  error.status = normalized.status
  error.data = normalized.data
  error.detail = normalized.detail
  error.normalized = normalized
  if (wasMessageRecentlyToasted(normalized.message)) {
    error.__toastShown = true
  }
  return Promise.reject(error)
}

// 核心API列表，这些API失败时会显示更友好的错误提示
const CRITICAL_APIS = [
  '/api/v1/auth/login',
  '/api/v1/auth/refresh-token',
  '/api/v1/auth/feishu/login',
  '/api/v1/auth/feishu/callback',
  '/api/v1/chat/send',
  '/api/v1/chat/create-session',
  '/api/v1/opportunities',
  '/api/v1/customers'
];

// 不需要token验证的接口白名单
const whiteList = [
  '/api/v1/auth/login',
  '/api/v1/auth/register',
  '/api/v1/auth/feishu/login',
  '/api/v1/auth/feishu/callback',
  '/api/v1/auth/feishu/autologin',
  '/api/v1/health',
  '/api/v1/tenants/public',
  '/api/v1/activation/activate',  // ✅ 激活账户接口
  '/api/v1/activation/resend-activation-email',  // ✅ 重发激活邮件接口
  '/api/v1/activation/check-activation-status',  // ✅ 检查激活状态接口
  '/api/v1/sms/',  // ✅ 短信验证码接口（注册页面使用，无需token）
  // '/api/v1/registration-applications'  // ✅ 注册申请接口（已注释，使用外部飞书表单）
]

console.log('🔧 API配置:', {
  runtime: typeof window !== 'undefined' && window.__ENV__ ? window.__ENV__.VITE_APP_API_BASE_URL : '未设置',
  buildtime: import.meta.env.VITE_APP_API_BASE_URL,
  final: getApiBaseUrl() || '(同源 /api 代理)'
});

// 创建axios实例
const service = axios.create({
  baseURL: getApiBaseUrl(),  // 使用动态获取的API地址
  timeout: DEFAULT_TIMEOUT, // 使用默认超时时间30秒
  withCredentials: false, // 关闭withCredentials，避免CORS问题
  maxRedirects: 5, // 允许最多5次重定向
  headers: {
    // 'Content-Type': 'application/json' // 移除默认 Content-Type
  },
  // 配置参数序列化，确保数组参数能够正确处理
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams()
      
      Object.keys(params).forEach(key => {
        const value = params[key]
        if (Array.isArray(value)) {
          // 对于数组参数，使用重复的键名格式
          // 例如: user_ids=1&user_ids=2&user_ids=3
          value.forEach(item => {
            if (item !== undefined && item !== null && item !== '') {
              searchParams.append(key, item)
            }
          })
        } else if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value)
        }
      })
      
      return searchParams.toString()
    }
  }
})


// 请求拦截器 - 动态设置超时时间
service.interceptors.request.use(
  config => {
    // 根据请求URL动态设置超时时间（幻觉检测优先匹配，避免被通用规则覆盖）
    const url = config.url || config.baseURL || '';
    if (url.includes('detect-hallucination')) {
      config.timeout = HALLUCINATION_DETECTION_TIMEOUT; // 幻觉检测：10分钟
    } else if (url.includes('hallucination-apply-fixes')) {
      config.timeout = HALLUCINATION_APPLY_FIXES_TIMEOUT; // 幻觉修复应用：10分钟
    } else if (url.includes('hallucination-task-status')) {
      config.timeout = HALLUCINATION_STATUS_TIMEOUT; // 幻觉状态轮询：2分钟
    } else if (url.includes('hallucination-history')) {
      config.timeout = HALLUCINATION_HISTORY_TIMEOUT; // 幻觉历史查询：2分钟
    } else if (config.url && config.url.includes('/targets/') && config.url.includes('/chat')) {
      config.timeout = TARGET_CHAT_TIMEOUT; // 靶点聊天请求使用10分钟超时
    } else if (config.url && config.url.includes('/chats/chat')) {
      config.timeout = QUICK_MODE_CHAT_TIMEOUT; // 快速模式简报生成（待分析靶点对话）
    } else if (config.url && config.url.includes('/chat')) {
      config.timeout = CHAT_TIMEOUT; // 其他聊天请求使用4分20秒超时
    } else if (config.url && config.url.includes('/agent-management/agents/test')) {
      config.timeout = AGENT_TEST_TIMEOUT; // ✅ 智能体测试使用60秒超时（LLM推理耗时较长）
    } else if (config.url && config.url.includes('/register')) {
      config.timeout = REGISTER_TIMEOUT; // 注册请求使用60秒超时
    } else if (config.url && config.url.includes('/activation/')) {
      config.timeout = ACTIVATION_TIMEOUT; // ✅ 激活请求使用60秒超时
    } else if (config.url && config.method === 'get' && /\/api\/v1\/targets(\?|$)/.test(config.url)) {
      config.timeout = TARGETS_LIST_TIMEOUT; // ✅ 靶点列表60秒（管理员/大数据量首次加载可能较慢）
    } else if (config.url && config.url.includes('/user-preferences') && config.url.includes('/trigger-extraction')) {
      config.timeout = PREFERENCE_EXTRACT_TIMEOUT; // ✅ 偏好抽取使用120秒超时（LLM分析耗时较长）
    } else if (config.url && config.url.includes('/user-preferences/extract')) {
      config.timeout = PREFERENCE_EXTRACT_TIMEOUT; // ✅ 偏好抽取使用120秒超时
    } else if (config.url && config.url.includes('/download-local')) {
      config.timeout = DOWNLOAD_LOCAL_TIMEOUT; // 本地下载使用3分钟超时
    } else if (config.url && config.url.includes('/download-proxy')) {
      config.timeout = DOWNLOAD_LOCAL_TIMEOUT; // 清洗版 PDF 实时生成可能较慢
    } else if (config.url && (config.url.includes('/upload') || config.url.includes('/download'))) {
      config.timeout = FILE_TIMEOUT; // 其他文件操作使用45秒超时
    } else if (!config._customTimeout) {
      config.timeout = DEFAULT_TIMEOUT;
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 添加重试机制
service.interceptors.response.use(null, async (err) => {
  const config = err.config;
  
  // 获取网络状态存储（在函数内获取store实例，而不是在模块顶层）
  let networkStore;
  try {
    networkStore = useNetworkStore();
  } catch (error) {
    console.warn('无法获取网络状态存储，可能是初始化前调用', error);
  }
  
  // 定义获取重试配置的函数
  const getRetryConfig = (config) => {
    if (!config) return null;
    
    // 如果配置中没有重试属性，则返回null
    if (config.retry === undefined) return null;
    
    // 如果还没有当前重试次数，则初始化为0
    if (config.currentRetry === undefined) {
      config.currentRetry = 0;
    }
    
    return {
      count: config.retry || 0,
      delay: config.retryDelay || 1000,
      currentRetry: config.currentRetry
    };
  };
  
  // 获取重试配置
  const retryConfig = getRetryConfig(config);
  
  // 检查是否是特殊错误类型，例如解包错误
  const isUnpackError = err.message && err.message.includes('too many values to unpack');
  
  // 检查是否是音频相关请求，这类请求需要特殊处理
  const isAudioRequest = config && config.url && config.url.includes('audio-to-text');
  
  // 如果是音频请求且出现解包错误，记录特殊日志
  if (isAudioRequest && isUnpackError) {
    console.warn('检测到音频请求解包错误，可能是移动设备兼容性问题');
    
    // 检查是否有重试配置且还有重试次数
    if (retryConfig && retryConfig.count > 0 && retryConfig.currentRetry < retryConfig.count) {
      console.log(`音频请求解包错误，准备第${retryConfig.currentRetry + 1}次重试，延迟${retryConfig.delay}ms`);
      
      // 检查请求体中是否有设备信息
      let isIPhone = false;
      if (config.data instanceof FormData) {
        try {
          const deviceInfo = config.data.get('deviceInfo');
          if (deviceInfo) {
            const deviceData = JSON.parse(deviceInfo);
            isIPhone = deviceData.isIOS;
            
            // 记录设备信息，用于排查问题
            console.log('设备信息:', deviceData);
          }
        } catch (e) {
          console.warn('解析设备信息失败', e);
        }
      }
      
      // 增加延迟时间，这有助于解决某些时序问题
      retryConfig.delay = isIPhone ? retryConfig.delay * 1.5 : retryConfig.delay;
      
      // 修改contentType，尝试使用不同格式进行重试
      const originalContentType = config.headers && config.headers['Content-Type'];
      if (isIPhone && config.data instanceof FormData) {
        try {
          // 尝试使用不同的contentType
          config.headers['Content-Type'] = 'multipart/form-data; boundary=----WebKitFormBoundary';
          console.log('修改ContentType为:', config.headers['Content-Type']);
        } catch (e) {
          console.warn('修改ContentType失败', e);
        }
      }
    
      // 执行重试
      return new Promise((resolve) => {
        setTimeout(() => {
          console.log(`开始第${retryConfig.currentRetry + 1}次重试...`);
          retryConfig.currentRetry += 1;
          resolve(service(config));
        }, retryConfig.delay);
      });
    }
  }
  
  // 超时错误特殊处理
  if (err.code === 'ECONNABORTED' && err.message && err.message.includes('timeout')) {
    console.warn('请求超时:', err.config?.url);
    let t
    try { ({ t } = useI18n()) } catch {}
    
    // 尝试获取网络状态存储
    if (networkStore) {
      networkStore.setNetworkError({
        type: 'timeout',
        message: (t && t('errors.timeout')) || '请求超时，请检查网络连接或稍后重试',
        url: err.config?.url
      });
    }
  }
  
  // 🔧 优化：网络连接错误处理（包括ECONNREFUSED等连接错误）
  if (err.message && (
    err.message.includes('Network Error') || 
    err.message.includes('Failed to fetch') ||
    err.code === 'ECONNREFUSED' ||
    err.message.includes('ECONNREFUSED')
  )) {
    console.warn('网络连接错误:', err.config?.url, err.message);
    let t
    try { ({ t } = useI18n()) } catch {}
    
    // GET 是幂等请求，可自动重试；mutation 必须由调用方显式声明可重试。
    const retryConfig = config?.retryConfig || { maxRetries: 3, retryDelay: 1000 };
    const currentRetry = config?.__retryCount || 0;
    const shouldRetry = canRetry(config, err)
      && currentRetry < retryConfig.maxRetries;
    
    if (shouldRetry) {
      const delay = retryConfig.retryDelay * Math.pow(2, currentRetry); // 指数退避
      console.log(`⚠️ 连接错误，${delay}ms后自动重试 (${currentRetry + 1}/${retryConfig.maxRetries}):`, err.config?.url);
      
      // 更新重试计数
      config.__retryCount = (config.__retryCount || 0) + 1;
      
      // 延迟后重试
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(service.request(config));
        }, delay);
      });
    }
    
    // 尝试获取网络状态存储
    if (networkStore) {
      networkStore.setNetworkError({
        type: 'connection',
        message: (t && t('errors.networkFailed')) || '网络连接错误，请检查您的网络',
        url: err.config?.url
      });
    }
  }
  
  // 服务器错误
  if (err.response && err.response.status >= 500) {
    console.error('服务器错误:', err.response.status, err.config?.url);
    let t
    try { ({ t } = useI18n()) } catch {}
    
    // 尝试获取网络状态存储
    if (networkStore) {
      networkStore.setNetworkError({
        type: 'server',
        message: (t && t('errors.serviceUnavailable')) || `服务器错误 (${err.response.status})，请稍后重试`,
        url: err.config?.url,
        status: err.response.status
      });
    }
  }
  
  // 文件操作相关API的特殊处理
  if (config && config.url && (config.url.includes('/api/v1/files/download/') || config.url.includes('/api/v1/files/'))) {
    console.error('文件操作失败:', err);
  }
  
  return Promise.reject(err);
})

// request拦截器
service.interceptors.request.use(
  async config => {
    // 检查是否是白名单接口（增强判断逻辑）
    const isWhiteList = whiteList.some(path => config.url && config.url.includes(path)) || 
                        config.isPublicApi === true || 
                        config.noAuth === true ||
                        config.skipAuth === true;  // ✅ 支持skipAuth参数
    
    // 如果是白名单接口，不需要token验证
    if (isWhiteList) {
      return config;
    }
    
    // 获取最新的token
    const token = getToken();

    if (token && !isValidJwtToken(token)) {
      console.warn('检测到无效 JWT token，清除认证信息并拒绝请求:', config.url)
      import('@/utils/auth').then(({ clearAuth }) => {
        clearAuth()
      })
      return Promise.reject(new Error('认证令牌无效，请重新登录'))
    }
    
    // 检查是否是飞书登录失效的状态
    const feishuLoginExpired = localStorage.getItem('feishu_login_expired');
    if (feishuLoginExpired === 'true' && config.url && !whiteList.some(path => config.url.includes(path))) {
      // 如果飞书登录已失效，且不是白名单请求，直接拒绝请求
      console.warn('飞书登录已失效，请求被拒绝:', config.url);
      return Promise.reject(new Error('登录状态已失效，请重新登录'));
    }
    
    // 如果token存在，则添加到请求头
    if (token) {
      // 确保headers对象存在
      config.headers = config.headers || {}
      config.headers['Authorization'] = `Bearer ${token}`
      
      // 检查token是否即将过期
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (payload.exp) {
            const expiryTime = payload.exp * 1000;
            const currentTime = Date.now();
            const remainingTime = (expiryTime - currentTime) / (1000 * 60); // 分钟
            
            // 如果token已过期
            if (currentTime > expiryTime) {
              console.error('Token已过期，立即清除认证信息并拒绝请求');
              
              // 清除过期的token
              import('@/utils/auth').then(({ clearAuth }) => {
                clearAuth();
              });
              
              // 拒绝请求
              return Promise.reject(new Error('Token已过期，请重新登录'));
            }
            // 如果token即将过期（小于5分钟）
            else if (remainingTime < 5) {
              console.warn(`Token即将过期，剩余${remainingTime.toFixed(2)}分钟`);
              // 记录即将过期的状态
              localStorage.setItem('token_expiring_soon', 'true');
              localStorage.setItem('token_expiring_time', expiryTime.toString());
            }
          }
        }
      } catch (error) {
        console.error('请求拦截器: 验证token失败', error);
      }
    }
    
    // 添加当前租户ID到请求头
    const currentTenantId = localStorage.getItem('current_tenant_id')
    if (currentTenantId) {
      config.headers = config.headers || {}
      config.headers['X-Tenant-ID'] = currentTenantId
    }
    
    // 添加用户语言偏好到请求头（支持后端国际化）
    const userLocale = localStorage.getItem('locale') || 'zh-CN'
    config.headers = config.headers || {}
    config.headers['Accept-Language'] = userLocale
    config.headers['X-Language'] = userLocale
      
    // 添加当前时间戳防止缓存
    const timestamp = new Date().getTime()
    if (config.method === 'get') {
      config.params = config.params || {}
      config.params._t = timestamp
    }
    
    if (!token) {
      console.warn('请求未携带token，可能导致权限不足:', config.url)
    }
    
    // console.log('请求拦截器:', config.url)
    
    // 添加飞书API特殊处理
    if (config.url && config.url.includes('/api/v1/auth/feishu')) {
      console.log('飞书API请求:', config.url)
      console.log('飞书请求参数:', config.params || config.data)
      
      // 确保飞书请求具有更长的超时时间
      config.timeout = 30000; // 30秒
      
      // 记录请求时间用于排查
      localStorage.setItem('last_feishu_request_time', getCurrentTime())
      localStorage.setItem('last_feishu_request_url', config.url)
    }
    
    // 租户公共API的特殊处理
    if (config.url && config.url.includes('/api/v1/tenants/public')) {
      console.log('公共租户API请求:', config.url);
      
      // 确保不添加认证信息
      if (config.headers) {
        // 强制删除Authorization头
        delete config.headers['Authorization'];
        // 删除租户头
        delete config.headers['X-Tenant-ID'];
      }
      
      // 标记为无需认证
      config.isPublicApi = true;
      config.showAuthError = false;
      
      // 记录租户请求时间
      localStorage.setItem('tenant_request_time', getCurrentTime());
      
      return config;
    }
    
    // 如果没有token且不是白名单接口，记录警告但不立即重定向
    if (!token && !isWhiteList) {
      console.warn('请求未携带token，可能导致权限不足:', config.url)
      
      // 特殊处理文件上传请求，避免上传时发现没有token
      if (config.url && (config.url.includes('/api/v1/files/upload') || config.url.includes('/upload'))) {
        console.error('文件上传请求缺少认证token')
        
        // 如果是el-upload组件的请求，我们需要拒绝它，避免发出未授权请求
        if (!config.fromAxiosInstance) {
          return Promise.reject(new Error('未登录或登录已过期，无法上传文件'))
        }
      }
      
      // 如果明确要求认证的请求，则拒绝请求
      if (config.requiresAuth === true) {
        return Promise.reject(new Error('未登录或登录已过期'))
      }
      
      // 其他情况继续请求，让响应拦截器处理401错误
    }
    
    // 获取认证store（在函数内获取，而不是在模块顶层）
    let authStore;
    try {
      authStore = useAuthStore();
    } catch (error) {
      console.warn('无法获取认证存储，可能是初始化前调用', error);
    }
    
    // 不同API设置不同的超时时间（仅在仍为默认值时兜底，避免覆盖前面的精细化配置）
    const currentTimeout = Number(config.timeout) || DEFAULT_TIMEOUT
    if (currentTimeout === DEFAULT_TIMEOUT) {
      if (config.url?.includes('/chat/')) {
        config.timeout = CHAT_TIMEOUT;
      } else if (config.url?.includes('/files/')) {
        config.timeout = FILE_TIMEOUT;
      }
    }
    
    // 配置文件相关请求的重试设置
    if (config.url && (config.url.includes('/api/v1/files/download/') || 
                        config.url.includes('/download-local') ||
                        (config.url.includes('/api/v1/files/') && config.method === 'delete'))) {
      config.retry = 2;  // 设置重试次数
      config.retryDelay = 2000;  // 设置重试延迟
    }
    
    // 确保params和data正确设置
    if (config.method === 'get' && config.params) {
      // console.log('GET请求参数(params):', config.params);
    }
    
    // 标记此请求是通过axios实例发起的
    config.fromAxiosInstance = true;
    
    return config
  },
  error => {
    console.error('请求配置错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 如果响应包含token，自动保存
    if (response.headers && response.headers['x-auth-token']) {
      const newToken = response.headers['x-auth-token'];
      setToken(newToken);
      console.log('从响应头获取到新token');
      
      // 清除token过期标记
      localStorage.removeItem('token_expired');
      localStorage.removeItem('token_expired_time');
      localStorage.removeItem('token_expiring_soon');
    }
    
    // 处理API响应
    const res = response.data;

    // 文件下载成功处理
    if (response.config?.url && response.config.url.includes('/api/v1/files/download/') && response.data && response.data.url) {
      console.log('文件下载成功，获取到URL:', response.data.url);
      
      // 使用文件下载URL打开新窗口或触发下载
      if (response.data.url && response.data.content_type !== 'text/plain') {
        window.open(response.data.url, '_blank');
      }
    }
    
    // 如果是文件下载，根据配置决定是否返回完整响应（用于读取响应头获取真实文件名）
    if (response.config && response.config.responseType === 'blob') {
      if (response.config.returnFullResponse === true) {
        return response;
      }
      return response.data;
    }
    
    // 记录API响应（调试用）
    if (response.config && response.config.url) {
      console.log(`✅ [API] ${response.config.url} | Response:`, res);
      console.log('API响应:', {
        url: response.config.url,
        status: response.status,
        statusText: response.statusText,
        data: res
      });
    }
    // 检查是否是成功的响应 目前接口返回体中没有code
    // if (res.code !== undefined && res.code !== 0 && res.code !== 200) {
    //   // 处理业务错误
    //   const errorMsg = res.message || res.msg || '未知错误';
      
    //   // 处理特定错误码
    //   if (res.code === 401 || res.code === 403) {
    //     // 认证失败或权限不足
    //     ElMessage.error(`认证失败: ${errorMsg}`);
    //     // 检查是否已经有重定向标记，避免多次重定向
    //     const redirecting = localStorage.getItem('auth_redirecting');
    //     if (!redirecting) {
    //       // 设置重定向标记，5秒后自动清除
    //       localStorage.setItem('auth_redirecting', 'true');
    //       setTimeout(() => localStorage.removeItem('auth_redirecting'), 5000);
          
    //       // 清除认证信息
    //       removeToken();
    //       removeUserInfo();
          
    //       // 跳转到登录页，使用路由名称而不是路径
    //       router.push({ name: 'Login' });
    //     }
        
    //     return Promise.reject(new Error(`认证失败: ${errorMsg}`));
    //   }
      
    //   // 其他业务错误
    //   ElMessage.error(errorMsg);
    //   return Promise.reject(new Error(errorMsg));
    // }
    
    // 返回成功响应
    return res;
  },
  async error => {
    // 错误处理
    let errMsg = error.message || '未知错误';
    
    // 请求是否为关键API
    const isCriticalApi = CRITICAL_APIS.some(api => error.config?.url?.includes(api));
    
    // 获取网络状态和认证存储（在函数内获取）
    let networkStore, authStore;
    try {
      networkStore = useNetworkStore();
    } catch (error) {
      console.warn('无法获取网络状态存储，可能是初始化前调用', error);
    }
    
    // 超时错误处理
    if (error.message?.includes('timeout')) {
      // 超时错误处理（多语言）
      errMsg = tOr('errors.timeout', '请求超时，请稍后再试');
      
      // 对于聊天API的特殊处理
      if (error.config?.url?.includes('/chat/')) {
        errMsg = tOr('errors.chatTimeout', '聊天服务响应超时，您的消息已发送，但可能需要更长时间处理');
        // 更新后端服务状态
        if (networkStore) {
          networkStore.setBackendAvailable(false);
        }
      }
      
      // 记录错误日志
      if (isCriticalApi) {
        console.error(`❌ [API] Timeout Error: ${error.config?.url}`);
      }
      
      return rejectApiError(error, errMsg);
    }
    
    // 网络错误处理
    if (!error.response) {
      // 网络连接问题（多语言）
      errMsg = tOr('errors.networkFailed', '网络连接失败，请检查您的网络连接');
      
      // 添加更详细的错误信息
      if (error.message.includes('Network Error')) {
        errMsg = tOr('errors.networkFailedWithBackend', '网络连接失败，请检查网络连接并确保后端服务已启动');
        console.error('详细网络错误:', error);
      } else if (error.code === 'ECONNABORTED') {
        errMsg = tOr('errors.serverSlow', '请求超时，服务器响应过慢');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('Unable to connect')) {
        errMsg = tOr('errors.unableConnect', '无法连接到服务器，请检查网络连接和后端服务状态');
      }
      
      // 更新网络状态
      if (networkStore) {
        networkStore.setOnline(false);
        networkStore.setBackendAvailable(false);
      }
      
      // 如果是核心API，提供更友好的提示
      if (isCriticalApi) {
        ElMessage.error({
          message: errMsg,
          duration: 5000
        });
      }
      
      return rejectApiError(error, errMsg);
    }
    
    // 检查是否是服务器断开连接错误（Dify 服务特有错误）
    if (error.message && (
        error.message.includes('Server disconnected without sending a response') ||
        error.message.includes('RemoteProtocolError')
    )) {
      console.warn('检测到Dify服务断开连接错误');
      // 返回一个降级响应，这样上层应用可以继续运行
      return Promise.reject({
        ...error,
        isDifyError: true,
        degraded: true,
        message: tOr('errors.difyDisconnected', '后端AI服务暂时不可用，系统将使用降级模式运行')
      });
    }
    
    // 检查是否是文件相关操作失败
    if (error.config && error.config.url) {
      // 文件下载失败
      if (error.config.url.includes('/api/v1/files/download/')) {
        ElMessage({
          message: error.response?.data?.detail || tOr('errors.fileDownloadFailed', '文件下载失败，请稍后重试'),
          type: 'error',
          duration: 5 * 1000
        });
      }
      // 文件删除失败
      else if (error.config.url.match(/\/api\/v1\/files\/\d+/) && error.config.method === 'delete') {
        ElMessage({
          message: error.response?.data?.detail || tOr('errors.fileDeleteFailed', '文件删除失败，请稍后重试'),
          type: 'error',
          duration: 5 * 1000
        });
      }
    }
    
    // 检查是否是网络错误（移动端常见）
    if (error.message && error.message.includes('Network Error')) {
      console.warn('检测到网络错误，可能是移动网络不稳定');
      ElMessage({
        message: tOr('errors.networkUnstable', '网络连接不稳定，请检查您的网络并重试'),
        type: 'warning',
        duration: 5 * 1000
      });
      return Promise.reject(error);
    }
    
    // 飞书相关错误的特殊处理
    if (error.config && error.config.url && error.config.url.includes('/api/v1/auth/feishu')) {
      console.error('飞书API错误:', {
        url: error.config.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      })
      
      // 记录飞书错误信息到localStorage，方便排查
      localStorage.setItem('last_feishu_error_time', getCurrentTime())
      localStorage.setItem('last_feishu_error_message', error.message)
      if (error.response) {
        localStorage.setItem('last_feishu_error_status', error.response.status)
        localStorage.setItem('last_feishu_error_data', JSON.stringify(error.response.data))
      }
      
      // 针对特定的飞书错误提供更具体的错误信息
      if (error.response && error.response.data) {
        const data = error.response.data
        if (data.code === 20029) {
          errMsg = tOr('errors.feishuIllegalRedirect', '飞书登录失败: redirect_uri 请求不合法，请联系管理员修改配置')
        } else if (data.code === 20030) {
          errMsg = tOr('errors.feishuCodeInvalid', '飞书登录失败: 授权码无效或已过期，请重新登录')
        } else if (data.code) {
          errMsg = `飞书登录失败: 错误码 ${data.code}, ${data.msg || '未知错误'}`
        }
      }
    }
    
    // 检查是否是网络错误（移动端常见）
    if (error.message && error.message.includes('Network Error')) {
      console.warn('检测到网络错误，可能是移动网络不稳定');
      ElMessage({
        message: tOr('errors.networkUnstable', '网络连接不稳定，请检查您的网络并重试'),
        type: 'warning',
        duration: 5 * 1000
      });
      return Promise.reject(error);
    }
    
    // HTTP错误处理
    const { status, data } = error.response;
    errMsg = normalizeApiError(error, errMsg).message
    const skipErrorNotification = (error.config && (
      error.config.noErrorNotification === true ||
      error.config.customErrorHandling === true
    ))
    if (!skipErrorNotification) {
      console.log('HTTP错误响应:', { status, data })
      if (import.meta.env.DEV) {
        console.error(`❌ [API] ${error.config?.url} | Error ${status}:`, data);
      }
    }
    
    // 使用防抖避免多次重定向到登录页
    // 创建一个全局变量用来存储最后跳转登录页的时间，避免同时多个401请求导致多次跳转
    if (!window._lastRedirectToLogin) {
      window._lastRedirectToLogin = 0;
    }

    if (status === 401) {
      // ✅ 检查是否是激活相关接口（直接返回错误，不进行token处理）
      if (error.config?.url?.includes('/activation/')) {
        console.log('激活接口返回401，直接返回错误信息，不处理token');
        return rejectApiError(error, '激活失败，请检查激活链接是否正确');
      }

      // 登录接口返回401时，优先透传后端业务错误文案（如账号或密码错误）
      if (error.config?.url?.includes('/api/v1/auth/login')) {
        return rejectApiError(error, '账号或密码错误，请核对后重试。')
      }
      
      // 🔒 检查是否禁用了401自动重试（例如Dify API）
      if (error.config?.skipAuthRetry === true) {
        console.log('401错误：跳过自动重试（skipAuthRetry=true）:', error.config?.url);
        return rejectApiError(error, tOr('errors.unauthorized', '未授权访问'));
      }
      
      // ========== 增强防抖机制：避免无限循环 ==========
      const now = Date.now();
      
      // 1. 检查是否已经有重定向标记（5秒内）
      const redirecting = localStorage.getItem('auth_redirecting');
      if (redirecting && (now - parseInt(redirecting)) < 5000) {
        console.log('401错误处理防抖：跳过重复处理（5秒内）');
        return Promise.reject(error);
      }
      
      // 2. 检查是否正在处理401错误（全局标记）
      if (window._is401Handling) {
        console.log('401错误处理防抖：已有其他请求正在处理401');
        return Promise.reject(error);
      }
      
      // 3. 设置全局标记，防止并发处理
      window._is401Handling = true;
      localStorage.setItem('auth_redirecting', now.toString());
      
      console.log('收到401错误，开始处理认证失败:', error.config?.url);
      
      // 4. 检查是否是特定的认证相关API（直接跳转，不尝试刷新）
      const isAuthApi = error.config?.url?.includes('/api/v1/auth/') || 
                        error.config?.url?.includes('/api/v1/users/me') ||
                        error.config?.url?.includes('/api/v1/menus/users/me');
      
      if (isAuthApi) {
        const currentPathOnly = router.currentRoute.value.path;
        if (currentPathOnly === '/activate') {
          console.log('公开激活页认证API返回401，不清除当前登录态:', error.config?.url);
          localStorage.removeItem('auth_redirecting');
          window._is401Handling = false;
          return rejectApiError(error, '认证失败，请重新登录');
        }

        console.log('认证相关API返回401，直接清除认证信息并跳转');
        // 清除认证信息
        removeToken();
        removeUserInfo();
        localStorage.removeItem('current_tenant_id');
        
        // 5秒后清除标记
        setTimeout(() => {
          localStorage.removeItem('auth_redirecting');
          window._is401Handling = false;
        }, 5000);
        
        // 跳转到登录页（排除公开页面）
        const currentPath = router.currentRoute.value.fullPath;
        const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/activate'];
        if (!publicPaths.includes(currentPathOnly)) {
          router.push({ 
            name: 'Login', 
            query: { redirect: currentPath } 
          });
        }
        
        return rejectApiError(error, '认证失败，请重新登录');
      }
      
      // 5. 对于非认证API的401错误，尝试验证token（但避免循环）
      try {
        const authStore = useAuthStore();
        
        // 如果是token验证请求本身失败，直接处理登出
        if (error.config?._isTokenValidation) {
          console.log('Token验证请求失败，直接执行登出');
          throw new Error('Token验证失败');
        }
        
        // 检查是否应该尝试验证token
        if (authStore && authStore.token && !authStore.isValidating) {
          console.log('尝试验证当前Token有效性:', error.config?.url);
          
          // 先验证当前token是否有效
          const isValid = await authStore.validateToken();
          if (isValid) {
            console.log('Token验证成功，重试原请求');
            // Token有效，重试原请求
            if (error.config) {
              // 清除标记
              localStorage.removeItem('auth_redirecting');
              window._is401Handling = false;
              return service(error.config);
            }
          } else {
            throw new Error('Token验证失败');
          }
        } else {
          throw new Error('未找到有效的认证信息或正在验证中');
        }
      } catch (refreshError) {
        console.error('Token验证失败，执行登出操作:', refreshError);
        
        // 清除认证信息
        removeToken();
        removeUserInfo();
        localStorage.removeItem('current_tenant_id');
        
        // 显示友好的错误提示
        ElMessage.error(tOr('errors.loginExpired', '登录已过期，请重新登录'));
        
        // 5秒后清除标记
        setTimeout(() => {
          localStorage.removeItem('auth_redirecting');
          window._is401Handling = false;
        }, 5000);
        
        // 跳转到登录页
        const currentPath = router.currentRoute.value.fullPath;
        if (currentPath !== '/login') {
          router.push({ 
            name: 'Login', 
            query: { redirect: currentPath } 
          });
        }
        
        return rejectApiError(error, '登录已过期，请重新登录');
      }
    }

    if (status === 403) {
      // ✅ 检查是否是激活相关接口（直接返回错误，不进行token处理）
      if (error.config?.url?.includes('/activation/')) {
        console.log('激活接口返回403，直接返回错误信息，不处理token');
        return rejectApiError(error, '激活失败，请检查激活链接是否正确');
      }
      
      // 🔧 优先使用后端返回的真实错误信息
      errMsg = normalizeApiError(error, tOr('errors.forbidden', '权限不足，无法访问该资源')).message;
      
      console.log('403错误详情:', {
        detail: data?.detail,
        url: error.config?.url,
        errorMessage: errMsg
      });
      
      // 检查是否是需要清除认证信息的场景
      // 只有特定的错误信息才清除token（避免误判）
      const shouldLogout = errMsg.includes('登录状态已失效') || 
                          errMsg.includes('登录已过期') || 
                          errMsg.includes('token已失效') ||
                          errMsg.includes('认证失败') ||
                          errMsg.includes('认证令牌') ||
                          errMsg.includes('无效的认证令牌') ||
                          errMsg.includes('请重新登录');
      
      if (shouldLogout) {
        if (router.currentRoute.value.path === '/activate') {
          console.log('公开激活页认证API返回403，不清除当前登录态:', error.config?.url);
          return rejectApiError(error, errMsg);
        }

        const currentTime = new Date().getTime()
        const lastErrorTime = localStorage.getItem('last_auth_error_time')
        
        // 限制提示频率，避免多个请求同时触发多个提示
        if (!lastErrorTime || (currentTime - parseInt(lastErrorTime)) > 5000) {
          localStorage.setItem('last_auth_error_time', currentTime.toString())
          
          try {
            // 立即清除认证信息
            removeToken()
            removeUserInfo()
            
            // 添加日志
            console.log('403错误（认证失效）：用户信息已清除，准备跳转到登录页')
            
            // 确保在清除信息后再跳转（排除公开页面）
            const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/activate'];
            if (!publicPaths.includes(router.currentRoute.value.path)) {
              router.push('/login').then(() => {
                console.log('成功跳转到登录页')
              }).catch(err => {
                console.error('跳转到登录页失败:', err)
                // 如果路由跳转失败，使用更直接的方式
                window.location.href = '/login'
              })
            }
            
          } catch (error) {
            console.error('退出登录过程出错:', error)
            // 发生错误时使用更直接的方式
            window.location.href = '/login'
          }
        }
      } else {
        // 对于其他403错误（如用户未激活、权限不足等），不清除token，只显示错误信息
        console.log('403错误（非认证问题）：', errMsg);
      }
      
      // 返回真实的错误信息
      return rejectApiError(error, errMsg);
    }
    if (status === 422) {
      const validationErrors = data?.debug?.details?.validation_errors
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const raw = validationErrors[0].msg || ''
        errMsg = raw.replace(/^Value error,\s*/, '')
      } else if (data?.error?.message) {
        errMsg = data.error.message
      } else if (typeof data?.detail === 'string') {
        errMsg = data.detail
      } else if (Array.isArray(data?.detail) && data.detail.length > 0) {
        const raw = data.detail[0].msg || ''
        errMsg = raw.replace(/^Value error,\s*/, '')
      } else {
        errMsg = normalizeApiError(error, tOr('errors.badRequest', '请求参数错误')).message
      }
    }
    if (status === 400) {
      if (typeof data?.detail === 'string' && data.detail.trim()) {
        errMsg = data.detail
      } else {
        errMsg = normalizeApiError(error, tOr('errors.badRequest', '请求参数错误')).message
      }
    }
    if (status === 409) {
      errMsg = normalizeApiError(error, tOr('errors.conflict', '资源冲突')).message;
    }
    if (status === 404) {
      errMsg = normalizeApiError(error, tOr('errors.notFound', '请求的资源不存在')).message;
    }
    if (status === 429) {
      // X-Coin额度不足或请求频率过高或会员限制
      // 优先显示后端返回的具体错误信息
      const rateLimitMessage = normalizeApiError(error, tOr('errors.tooManyRequestsOrNoBalance', '请求过于频繁或X-Coin额度不足，请稍后重试')).message
      if (rateLimitMessage) {
        errMsg = rateLimitMessage;
        // 如果是余额不足错误，特殊处理样式
        if (rateLimitMessage.includes('X-Coin额度不足') || rateLimitMessage.includes('X-Coin额度已禁用')) {
          // 触发全局余额警告事件
          window.dispatchEvent(new CustomEvent('balance-insufficient', {
            detail: {
              message: rateLimitMessage,
              type: 'error'
            }
          }));
        } 
        // 如果是会员限制错误（并发、知识库等），触发会员限制事件
        else if (rateLimitMessage.includes('并发') || rateLimitMessage.includes('知识库') || rateLimitMessage.includes('同时生成')) {
          window.dispatchEvent(new CustomEvent('membership-limit-exceeded', {
            detail: {
              error: error,
              message: rateLimitMessage,
              type: 'limit',
              headers: error.response?.headers
            }
          }));
        }
      } else {
        errMsg = tOr('errors.tooManyRequestsOrNoBalance', '请求过于频繁或X-Coin额度不足，请稍后重试');
      }
    }
    if (status === 500) {
      errMsg = normalizeApiError(error, tOr('errors.serverInternal', '服务器内部错误')).message;
    }
    if (status >= 500) {
      // 尝试获取网络状态存储
      try {
        const networkStore = useNetworkStore();
        networkStore.setBackendAvailable(false);
      } catch (error) {
        console.warn('无法获取网络状态存储');
      }
      errMsg = tOr('errors.serviceUnavailable', '服务暂时不可用，请稍后再试');
    }
    if (!skipErrorNotification) {
      errMsg = status === 403 ? errMsg : normalizeApiError(error, errMsg).message
      if (notifyApiErrorOnce(errMsg)) {
        error.__toastShown = true
      }
    } else {
      console.log('跳过错误提示，由调用方处理:', error.config?.url)
    }

    return rejectApiError(error, errMsg);
  }
)

// 导出封装的axios实例
export default service

// 导出更便捷的API调用方法
export const http = {
  get: (url, params, config = {}) => service.get(url, { params, ...config }),
  post: (url, data, config = {}) => service.post(url, data, config),
  put: (url, data, config = {}) => service.put(url, data, config),
  delete: (url, config = {}) => service.delete(url, config),
};

// 导出异步检查后端健康状态的方法
export const checkBackendHealth = async () => {
  // 获取网络状态存储（在函数内获取）
  let networkStore;
  try {
    networkStore = useNetworkStore();
  } catch (error) {
    console.warn('无法获取网络状态存储，可能是初始化前调用', error);
    return false;
  }
  
  // try {
  //   await service.get('/api/v1/health-check', { timeout: 5000 });
  //   networkStore.setBackendAvailable(true);
  //   return true;
  // } catch (error) {
  //   networkStore.setBackendAvailable(false);
  //   return false;
  // }
};

// 添加处理分页参数的工具函数
export const normalizePageParams = (params) => {
  if (!params) return {};
  
  const result = { ...params };
  
  // 处理页码参数: page/currentPage -> page_num 或 skip
  if (result.page !== undefined && result.page_num === undefined) {
    // 前端使用page，转换为后端的page_num
    result.page_num = result.page;
  } else if (result.currentPage !== undefined && result.page_num === undefined && result.page === undefined) {
    // 前端使用currentPage，转换为后端的page_num
    result.page_num = result.currentPage;
  }
  
  // 处理每页数量参数: pageSize -> page_size 或 limit
  if (result.pageSize !== undefined && result.page_size === undefined) {
    // 前端使用pageSize，转换为后端的page_size和limit
    result.page_size = result.pageSize;
    result.limit = result.pageSize;
  }
  
  // 计算skip参数 - 页码从1开始，转换为skip从0开始
  // 确保使用正确的页码和每页数量参数
  const page = result.page_num || result.page || result.currentPage || 1;
  const size = result.page_size || result.pageSize || result.limit || 10;
  
  // 计算跳过的记录数
  result.skip = (page - 1) * size;
  // 确保limit参数存在
  result.limit = size;
  
  console.log('规范化后的分页参数:', result);
  
  return result;
};
